import { ObjectWithUid } from "../utils.tsx";
import { ResponseWrapper, PagedResults, trackerGet, trackerPost, trackerDelete, trackerPatch, decodeResult } from "./basicApi.ts";
import { ScanTypeSlug } from '../scanTypes.ts';


interface SampleDto extends ObjectWithUid {
  slug: string;
  // Intended to be short and unique, but this is not strictly enforced.
  name: string;
  // Meant to be longer than the name.  Can be blank.
  description: string;
  // Unique slug identifier of the set this config belongs to.
  slug_set: string;
  // ESAF the set belongs to, denormalized from the set record.
  slug_esaf: string;
  slug_scan_type: ScanTypeSlug;
  scan_status: string;
  id_most_recent_editor: number;
  date_last_modified: string;
}


interface SampleCreateDto {
  slug?: string;
  // Intended to be short and unique, but this is not strictly enforced.
  name: string;
  // Meant to be longer than the name.  Can be blank.
  description: string;
  // Unique slug identifier of the set this config belongs to.
  slug_set: string;
  slug_scan_type: ScanTypeSlug;
}


interface SampleUpdateDto {
  slug: string;
  // Intended to be short and unique, but this is not strictly enforced.
  name?: string;
  // Meant to be longer than the name.  Can be blank.
  description?: string;
  slug_set?: string;
  slug_scan_type?: ScanTypeSlug;
  scan_status?: string;
}


// Special DTO for an endpoint that mass-sets parameter values by name.
interface SampleSetParametersByNameDto {
  create_parameters_if_missing?: boolean;
  add_parameters_to_scan_type_if_missing?: boolean;
  allow_parameters_not_in_scan_type?: boolean;
  remove_other_values?: boolean;
  values: {[key: string]: string;};
}


// Special DTO for fetching all the parameter values for a single sample.

interface SampleGetParametersByNameDto extends ObjectWithUid {
  slug: string;
  // Intended to be short and unique, but this is not strictly enforced.
  name: string;
  // Unique slug identifier of the set this config belongs to.
  slug_set: string;
  // Parameter values may be given for Parameters that are not in the currently chosen ScanType.
  // This is not strictly enforced, so old inapplicable values can be preserved
  // in case a previous ScanType is re-selected.
  // If the Parameters are from entirely different Beamlines, the names may conflict, but that is
  // definitely not a use case we want to support.
  parameter_values_by_name: {[key: string]: any};
}


// Get the Samples for the given ESAF the logged-in user has access to
async function getSamplesForEsaf(slug: string): Promise<ResponseWrapper<PagedResults<SampleDto>>> {
  const params = { set__esaf__slug: slug };
  const result = await trackerGet(`samples/`, params);
  return decodeResult(result);
}


async function getSampleBySlug(slug: string): Promise<ResponseWrapper<SampleDto>> {
  const result = await trackerGet(`samples/${slug}/`, {});
  return decodeResult(result);
}


// Create a Sample record on the server
async function createSample(dto: SampleCreateDto): Promise<ResponseWrapper<SampleDto>> {
  const result = await trackerPost(`samples/`, JSON.stringify(dto));
  return decodeResult(result);
}


// Update a Sample record on the server.
async function updateSample(dto: SampleUpdateDto): Promise<ResponseWrapper<SampleDto>> {
  const result = await trackerPatch(`samples/${dto.slug}/`, JSON.stringify(dto));
  return decodeResult(result);
}


// Create or update ParameterValues for the given sample, using a dictionary of parameter name to value.
async function setSampleParameterValuesByName(slug:string, dto: SampleSetParametersByNameDto): Promise<ResponseWrapper<SampleDto>> {
  const result = await trackerPost(`samples/${slug}/set_values_by_name/`, JSON.stringify(dto));
  return decodeResult(result);
}


// Delete a Sample record from the server, returning the Id used if successful.
async function deleteSample(sampleSlug: string): Promise<ResponseWrapper<string>> {
  const result = await trackerDelete(`samples/${sampleSlug}/`, {});
 if (result.success) {
   return { success: true, response: sampleSlug };
 }
 return { success: false, message: result.message };
}


export type { SampleDto, SampleCreateDto, SampleUpdateDto, SampleGetParametersByNameDto, SampleSetParametersByNameDto };
export { getSamplesForEsaf, getSampleBySlug, createSample, updateSample, deleteSample, setSampleParameterValuesByName }
